import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import axiosWithAuth from '../../helpers/AxiosHelper';
import errorMessage from '../../helpers/errorMessage';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import PageHeader from '../common/PageHeader';
import EmptyState from '../common/EmptyState';
import LoadingState from '../common/LoadingState';
import './investment_reconciliation.css';

// --- FORMAT DATE ---
const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// --- FORMAT AMOUNT ---
const formatAmount = (value) => {
  const num = value ?? 0;
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// --- EXPORT TO EXCEL ---
const exportToExcel = (rows, fileName = 'investment_data.xlsx', columns = []) => {
  if (!rows || rows.length === 0) return;

  const exportData = rows.map(row => {
    const record = {};
    columns.forEach(col => {
      // Use header as the Excel column name
      const header = col.header || col.field;

      if (col.body) {
        record[header] = typeof col.body === 'function' ? col.body(row) : row[col.field];
      } else {
        record[header] = row[col.field];
      }
    });
    return record;
  });

  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
  saveAs(data, fileName);
};




const InvestmentReconciliation = () => {
  const [selectedAccountGroup, setSelectedAccountGroup] = useState('E98');
  const [selectedPlatform, setSelectedPlatform] = useState('ALL');
  const [data, setData] = useState({ E98: [], E52: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [error, setError] = useState(null);
  // The tiles and tables below read an empty result as "no mismatches" -
  // so they're only shown once the fetch has actually succeeded; before
  // that (or after a failure) zero counts would be misleading.
  const [loaded, setLoaded] = useState(false);

  const accountGroups = [
    { label: 'E98', value: 'E98' },
    { label: 'E52', value: 'E52' },
  ];

  // --- DYNAMIC PLATFORMS ---
  const platforms = useMemo(() => {
    const rows = Object.values(data).flat();
    const uniquePlatforms = [...new Set(rows.map(r => r.account?.toUpperCase()))];
    return [
      { label: 'ALL', value: 'ALL' },
      ...uniquePlatforms.map(p => ({ label: p, value: p }))
    ];
  }, [data]);

  // --- FETCH DATA ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axiosWithAuth.get('/loan-check');
        const formatted = res.data.fromLoansweeper || { E98: [], E52: [] };
        setData(formatted);
        setLoaded(true);
      } catch (err) {
        setError(errorMessage(err, 'Failed to load reconciliation data.'));
      }
    };
    fetchData();
  }, []);

  // --- FILTERED ROWS ---
  const filteredRows = useMemo(() => {
    let rows = data[selectedAccountGroup] || [];

    if (selectedPlatform !== "ALL") {
      rows = rows.filter(r => r.account?.toUpperCase() === selectedPlatform);
    }

    if (selectedDate) {
      const selectedDateString = new Date(selectedDate).toDateString();
      rows = rows.filter(r => new Date(r.investedDate).toDateString() === selectedDateString);
    }

    return rows;
  }, [data, selectedAccountGroup, selectedPlatform, selectedDate]);

  const filteredPlatform = useMemo(() => filteredRows.filter(r => r.source === "PlatformOnly"), [filteredRows]);
  const filteredLoansweeper = useMemo(() => filteredRows.filter(r => r.source === "LoansweeperOnly"), [filteredRows]);
  const filteredDateMismatch = useMemo(() => filteredRows.filter(r => r.source === "DateMismatch"),[filteredRows]);
  const filteredAmountMismatch = useMemo(() => filteredRows.filter(r => r.source === "AmountMismatch"),[filteredRows]);




  


  // --- RENDER TABLE ---
  const renderTable = (rows, fileName, columns) => {
    // An empty result list means no rows of this type were returned.
    if (!rows.length) {
      return (
        <EmptyState
          compact
          icon="pi-check-circle"
          title="No mismatches"
          message="No loans of this type were returned for the selected account and Originator."
        />
      );
    }

    return (
      <div className="table-wrapper">
        <DataTable
          value={rows}
          paginator
          rows={5}
          scrollable
          scrollHeight="400px"
          size="small"
          className="full-width-table"
          sortMode="multiple"
          multiSortMeta={[
            { field: 'account', order: 1 },
            { field: 'investedDate', order: -1 }
          ]}
        >
          {columns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sortable}
              body={col.body}
              headerClassName={col.numeric ? 'lc-num' : undefined}
              bodyClassName={col.numeric ? 'lc-num' : undefined}
            />
          ))}
        </DataTable>
      </div>
    );
  };

  const platform_ls_Columns = [
    { field: 'account', header: 'Originator', sortable: true },
    { field: 'loanId', header: 'Loan ID' },
    { field: 'loanType', 
      header: 'Loan Type', 
      body: (rowData) => {
        if (!rowData.loanType) return '';
        return rowData.loanType.toLowerCase() === 'sme'
          ? 'SME'
          : rowData.loanType.charAt(0).toUpperCase() + rowData.loanType.slice(1);
      }
    },
    { field: 'investedDate', header: 'Invested Date', body: (rowData) => formatDate(rowData.investedDate), sortable: true }
  ];

  const amountMismatchColumns = [
    { field: 'account', header: 'Originator', sortable: true },
    { field: 'loanIdLS', header: 'Loan ID' },
    { field: 'amountPlatform', header: 'Amount Originator', numeric: true, body: (rowData) => formatAmount(Math.round(rowData.amountPlatform ?? 0)),},
    { field: 'amountLS', header: 'Amount LS', numeric: true, body: (rowData) => formatAmount(Math.round(rowData.amountLS ?? 0))}
  ];

    const dateMismatchColumns = [
    { field: 'account', header: 'Originator', sortable: true },
    { field: 'loanIdLS', header: 'Loan ID' },
    { field: 'fullDatePlatform', header: 'Date Originator',body: (rowData) => formatDate(rowData.fullDatePlatform)},
    { field: 'fullDateLS', header: 'Date LS',body: (rowData) => formatDate(rowData.fullDateLS)}
  ];

// REUSABLE TABLE BLOCK
  // `title` keeps the original text (used as the accessible name);
  // `heading` is the visual version with readable present/missing chips.
  const TableBlock = ({ title, heading, subtitle, data, fileName, columns }) => {
    return (
      <section className="ex-section table-section" aria-label={title}>
        <div className="ex-section-header">
          <div className="lc-card-heading">
            <h3 className="ex-section-title investment-headline">
              {heading || title}
              <span
                className={`lc-count ${data.length ? 'is-open' : 'is-clear'}`}
                title={`${data.length} ${data.length === 1 ? 'row' : 'rows'}`}
              >
                {data.length}
              </span>
            </h3>
            {subtitle && <div className="ex-section-subtitle">{subtitle}</div>}
          </div>
          <Button
            label="Download XLSX"
            icon="pi pi-download"
            className="p-button-outlined p-button-sm"
            disabled={!data.length}
            onClick={() => exportToExcel(data, fileName, columns)}
          />
        </div>
        {renderTable(data, fileName, columns)}
      </section>
    );
  };

  // Visual "✓ in / ✗ not in" chip for a source database.
  const SourceChip = ({ present, label }) => (
    <span className={`lc-source ${present ? 'is-present' : 'is-missing'}`}>
      <i className={`pi ${present ? 'pi-check' : 'pi-times'}`} aria-hidden="true" />
      {label}
    </span>
  );

  const summaryTiles = [
    { label: 'Originator only', hint: 'Missing in Loansweeper DB', icon: 'pi-database', count: filteredPlatform.length },
    { label: 'Date mismatch', hint: 'Dates differ', icon: 'pi-calendar-times', count: filteredDateMismatch.length },
    { label: 'Amount mismatch', hint: 'Amounts differ', icon: 'pi-euro', count: filteredAmountMismatch.length },
    { label: 'Loansweeper only', hint: 'Missing in Originator DB', icon: 'pi-server', count: filteredLoansweeper.length },
  ];

  return (
    // <div className="reconciliation-container">
    <div className="content-container">
      <div style={{ maxWidth: "100%" }}>

      <PageHeader
        title="Investment Reconciliation"
        subtitle="Loans that differ between the Originator's records and Loansweeper."
        icon="pi-sync"
      />

      {error && (
        <div className="ex-inline-error lc-error">{error}</div>
      )}

      {/* Filters */}
      <div className="filters ex-toolbar investment-filters">
        <div className="ex-toolbar-group">
          <div className="investment-filter">
            <label className="account-label" htmlFor="lc-account">Account</label>
            <Dropdown
              inputId="lc-account"
              value={selectedAccountGroup}
              options={accountGroups}
              onChange={(e) => setSelectedAccountGroup(e.value)}
              placeholder="Select Account Group"
              className="account-dropdown"
            />
          </div>

          <div className="investment-filter">
            <label className="account-label" htmlFor="lc-platform">Originator</label>
            <Dropdown
              inputId="lc-platform"
              value={selectedPlatform}
              options={platforms}
              onChange={(e) => setSelectedPlatform(e.value)}
              placeholder="Select Originator"
              className="account-dropdown"
            />
          </div>
        </div>
        <div className="lc-filter-note">
          <i className="pi pi-filter" aria-hidden="true" />
          Showing {selectedAccountGroup} · {selectedPlatform === 'ALL' ? 'all Originators' : selectedPlatform}
        </div>
      </div>

      {!loaded && !error && <LoadingState label="Loading reconciliation data" />}

      {loaded && <>
      {/* Summary: row counts of the tables below */}
      <div className="ex-stat-grid lc-stats">
        {summaryTiles.map(tile => (
          <div key={tile.label} className={`ex-stat lc-stat ${tile.count ? 'is-open' : 'is-clear'}`}>
            <span className="ex-stat-icon" aria-hidden="true"><i className={`pi ${tile.icon}`} /></span>
            <div className="ex-stat-body">
              <div className="ex-stat-label">{tile.label}</div>
              <div className="ex-stat-value">{tile.count}</div>
              <div className="ex-stat-hint">{tile.hint}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tables */}
      <TableBlock
        title="Originator DB ✓ | Loansweeper DB ✗"
        heading={<><SourceChip present label="Originator DB" /><span className="lc-sep">|</span><SourceChip label="Loansweeper DB" /></>}
        subtitle="Loans in the Originator's records that are not in Loansweeper."
        data={filteredPlatform}
        fileName="Platform_DB.xlsx"
        columns={platform_ls_Columns}
      />

      <TableBlock
        title="Date Mismatch"
        subtitle="Loans in both databases whose dates differ."
        data={filteredDateMismatch}
        fileName="Date_Mismatch.xlsx"
        columns={dateMismatchColumns}
      />

      <TableBlock
        title="Amount Mismatch"
        subtitle="Loans in both databases whose amounts differ."
        data={filteredAmountMismatch}
        fileName="Amount_Mismatch.xlsx"
        columns={amountMismatchColumns}
      />

      <TableBlock
        title="Loansweeper DB ✓ | Originator DB ✗"
        heading={<><SourceChip present label="Loansweeper DB" /><span className="lc-sep">|</span><SourceChip label="Originator DB" /></>}
        subtitle="Loans in Loansweeper that are not in the Originator's records."
        data={filteredLoansweeper}
        fileName="Loansweeper_DB.xlsx"
        columns={platform_ls_Columns}
      />
      </>}
    </div>
  </div>
  );
}

export default InvestmentReconciliation;